import { AlertTriangle, MessageSquareWarning, Info } from "lucide-react";
import { Button } from "@/v2/components/ui/button";
import { cn } from "@/v2/lib/utils";

const variants = {
	error: {
		container: "border-destructive/35 bg-destructive/10",
		icon: "text-destructive",
		title: "text-destructive",
		body: "text-destructive/80",
		Icon: AlertTriangle,
	},
	warning: {
		container: "border-warning/35 bg-warning-soft",
		icon: "text-warning",
		title: "text-warning",
		body: "text-warning/80",
		Icon: MessageSquareWarning,
	},
	info: {
		container: "border-info/35 bg-info-soft",
		icon: "text-info",
		title: "text-info",
		body: "text-info/80",
		Icon: Info,
	},
} as const;

interface BannerProps {
	variant: keyof typeof variants;
	title: string;
	children: React.ReactNode;
	action?: {
		label: string;
		onClick: () => void;
		loading?: boolean;
	};
	className?: string;
}

export function Banner({ variant, title, children, action, className }: BannerProps) {
	const v = variants[variant];

	return (
		<div className={cn("rounded-md border p-3", v.container, className)}>
			<div className="flex items-start gap-2">
				<v.Icon className={cn("h-4 w-4 mt-0.5 shrink-0", v.icon)} />
				<div className="flex-1 min-w-0">
					<p className={cn("text-sm font-medium", v.title)}>{title}</p>
					<div className={cn("mt-1 text-sm", v.body)}>{children}</div>
				</div>
				{action && (
					<Button
						type="button"
						variant="outline"
						size="sm"
						onClick={action.onClick}
						disabled={action.loading}
						className="shrink-0"
					>
						{action.loading ? "..." : action.label}
					</Button>
				)}
			</div>
		</div>
	);
}
